import * as _ from 'lodash-es';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { UserType } from '../graphql/generated/graphql';
import {
  getDirectMembers,
  getDirectOwners,
  getTopicUserIdSet,
} from './utils/getTopicUsers';

const TOPIC_POLICY_CHECK_TOPIC_FRAGMENT = gql(`
  fragment topicPolicyCheck_topic on Topic {
    id
    privacy
    memberEditable
    parent {
      id
    }
    ...getTopicUserIdSet_topic
  }
`);

const TOPIC_POLICY_CHECK_USER_FRAGMENT = gql(`
  fragment topicPolicyCheck_user on User {
    id
    type
  }
`);

type TopicFragment = DocumentType<typeof TOPIC_POLICY_CHECK_TOPIC_FRAGMENT>;

type TopicPolicyCheckUser =
  | UserRole
  | TopicRole
  | DocumentType<typeof TOPIC_POLICY_CHECK_USER_FRAGMENT>;

export enum UserRole {
  Anonymous = 'AnonymousUser',
  Guest = 'GuestUser',
  Full = 'FullUser',
}

export enum TopicRole {
  Member = 'TopicMember',
  Owner = 'TopicOwner',
}

type BaseUser = Pick<
  DocumentType<typeof TOPIC_POLICY_CHECK_USER_FRAGMENT>,
  'id'
>;
type User = UserRole | TopicRole | BaseUser;

export enum MinimalRoleLevel {
  None = 'None',
  Member = 'Member',
  Owner = 'Owner',
  Never = 'Never',
}

export enum TopicPolicyAction {
  AddSubtopic,
  AddRemoveMember,
  AddRemoveOwner,
  DeleteTopic,
  EditPost,
  EditTopic,
  ViewPost,
  ReorganizeTopic,
}

export const MEMBER_EDITABLE_ALL = 'ALL';
export const MEMBER_EDITABLE_POST = 'POST';
export const MEMBER_EDITABLE_NONE = 'NONE';

export const isRegisteredUser = (user: User) =>
  typeof user === 'object' ? !!user.id : user !== UserRole.Anonymous;

export const isFullUser = (user: TopicPolicyCheckUser) =>
  typeof user === 'object'
    ? user.type !== UserType.Guest
    : user === UserRole.Full;

export const isOwner = (
  topic: Parameters<typeof getTopicUserIdSet>[0],
  user: User,
) =>
  typeof user === 'object'
    ? getTopicUserIdSet(topic).owners.has(user.id)
    : user === TopicRole.Owner;

export const isMember = (
  topic: Parameters<typeof getTopicUserIdSet>[0],
  user: User,
) =>
  typeof user === 'object'
    ? getTopicUserIdSet(topic).members.has(user.id)
    : user === TopicRole.Member;

const isDirectMember = (topic: TopicFragment, user: BaseUser) =>
  getDirectOwners(topic)
    .map(({ id }) => id)
    .includes(user.id);

const isDirectOwner = (topic: TopicFragment, user: BaseUser) =>
  getDirectMembers(topic)
    .map(({ id }) => id)
    .includes(user.id);

const IS_REQUESTOR_TOPIC_FRAGMENT = gql(`
  fragment isRequestor_topic on Topic {
    id
    requestors {
      id
    }
  }
`);

export const isRequestor = (
  { requestors }: DocumentType<typeof IS_REQUESTOR_TOPIC_FRAGMENT>,
  { id }: { id: string },
) => _.some(requestors, { id });

export const isUserInTopic = (topic: TopicFragment, user: User) =>
  isOwner(topic, user) || isMember(topic, user);

export const isUserDirectlyInTopic = (topic: TopicFragment, user: BaseUser) =>
  isDirectOwner(topic, user) || isDirectMember(topic, user);

export function getTopicPolicyRequirement(
  action: TopicPolicyAction,
  topic: TopicFragment,
  user: TopicPolicyCheckUser,
): MinimalRoleLevel {
  const { memberEditable, privacy } = topic;
  const { None, Never, Owner, Member } = MinimalRoleLevel;
  const isFull = isFullUser(user);
  const isFullUserOrMember = isFull ? None : Member;

  switch (action) {
    case TopicPolicyAction.AddSubtopic:
      return memberEditable !== MEMBER_EDITABLE_ALL
        ? Owner
        : {
            OPEN: isFullUserOrMember,
            PUBLIC: isFullUserOrMember,
            PRIVATE: Member,
            SECRET: Member,
          }[privacy];
    case TopicPolicyAction.AddRemoveMember:
      return memberEditable !== MEMBER_EDITABLE_ALL
        ? Owner
        : {
            OPEN: isFullUserOrMember,
            PUBLIC: isFullUserOrMember,
            PRIVATE: Member,
            SECRET: Member,
          }[privacy];
    case TopicPolicyAction.AddRemoveOwner:
      return Owner;
    case TopicPolicyAction.DeleteTopic:
      return topic.parent || isFull
        ? getTopicPolicyRequirement(TopicPolicyAction.EditTopic, topic, user)
        : Never;
    case TopicPolicyAction.EditPost:
      return memberEditable === MEMBER_EDITABLE_NONE
        ? Owner
        : {
            OPEN: isFullUserOrMember,
            PUBLIC: isFullUserOrMember,
            PRIVATE: Member,
            SECRET: Member,
          }[privacy];
    case TopicPolicyAction.EditTopic:
      return memberEditable === MEMBER_EDITABLE_ALL ? Member : Owner;
    case TopicPolicyAction.ViewPost:
      return {
        OPEN: isFullUserOrMember,
        PUBLIC: None,
        PRIVATE: Member,
        SECRET: Member,
      }[privacy];
    case TopicPolicyAction.ReorganizeTopic:
      return Owner;
    default:
      return Never;
  }
}

function topicPolicyCheck(
  action: TopicPolicyAction.DeleteTopic,
  topic: TopicFragment,
  user: Exclude<TopicPolicyCheckUser, TopicRole>,
): boolean;
function topicPolicyCheck(
  action: Exclude<TopicPolicyAction, TopicPolicyAction.DeleteTopic>,
  topic: TopicFragment,
  user: TopicPolicyCheckUser,
): boolean;
function topicPolicyCheck(
  action: TopicPolicyAction,
  topic: TopicFragment,
  user: TopicPolicyCheckUser,
): boolean {
  switch (getTopicPolicyRequirement(action, topic, user)) {
    case MinimalRoleLevel.None:
      return true;
    case MinimalRoleLevel.Member:
      return isUserInTopic(topic, user);
    case MinimalRoleLevel.Owner:
      return isOwner(topic, user);
    case MinimalRoleLevel.Never:
    default:
      return false;
  }
}

export default topicPolicyCheck;
